import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, CheckCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // If no token, show forgot password form
  if (!token) return <ForgotPasswordForm />;

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally { setLoading(false); }
  };

  if (success) return (
    <div className="min-h-screen flex items-center justify-center bg-cream pt-16 p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
        <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
        <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Password Reset!</h2>
        <p className="font-dm text-navy/60 text-sm mb-6">You can now log in with your new password.</p>
        <Link to="/login" className="inline-block bg-navy text-white font-dm font-bold px-8 py-3 rounded-lg">Go to Login</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream pt-16 p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8">
        <h2 className="font-playfair text-navy text-2xl font-bold mb-2 text-center">Set New Password</h2>
        <p className="font-dm text-navy/50 text-sm text-center mb-6">Enter your new password below</p>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 font-dm text-sm px-4 py-3 rounded-lg mb-4">{error}</div>}
        <form onSubmit={handleReset} className="space-y-4">
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30" />
            <input type="password" required placeholder="New Password" value={password} onChange={e => setPassword(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-navy/20 rounded-lg font-dm text-sm focus:border-gold focus:outline-none" />
          </div>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30" />
            <input type="password" required placeholder="Confirm New Password" value={confirm} onChange={e => setConfirm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-navy/20 rounded-lg font-dm text-sm focus:border-gold focus:outline-none" />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-navy text-white font-dm font-bold py-3 rounded-lg hover:bg-gold hover:text-navy transition-colors disabled:opacity-50">
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  if (sent) return (
    <div className="min-h-screen flex items-center justify-center bg-cream pt-16 p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
        <Mail className="w-14 h-14 text-gold mx-auto mb-4" />
        <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Check Your Email</h2>
        <p className="font-dm text-navy/60 text-sm mb-6">If an account exists for {email}, we've sent a password reset link. Check your inbox (and spam folder).</p>
        <Link to="/login" className="inline-block border border-navy/20 text-navy font-dm px-6 py-2.5 rounded-lg hover:bg-navy/5">Back to Login</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream pt-16 p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8">
        <h2 className="font-playfair text-navy text-2xl font-bold mb-2 text-center">Forgot Password</h2>
        <p className="font-dm text-navy/50 text-sm text-center mb-6">Enter your email and we'll send you a reset link</p>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 font-dm text-sm px-4 py-3 rounded-lg mb-4">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30" />
            <input type="email" required placeholder="Your email address" value={email} onChange={e => setEmail(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-navy/20 rounded-lg font-dm text-sm focus:border-gold focus:outline-none" />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-navy text-white font-dm font-bold py-3 rounded-lg hover:bg-gold hover:text-navy transition-colors disabled:opacity-50">
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
        </form>
        <p className="text-center mt-4 font-dm text-sm text-navy/50">
          <Link to="/login" className="text-gold hover:underline">Back to Login</Link>
        </p>
      </motion.div>
    </div>
  );
}
